import { EventMembershipStatuses } from "@api/enums/EventMembershipStatuses";
import { EventMembershipTypes } from "@api/enums/EventMembershipTypes";
import { UserCreateParams } from "@api/models/interfaces/user/UserCreateParams";
import { UserProfileInterface } from "@api/models/interfaces/UserProfileInterface";
import { User } from "@api/models/User";
import { UserProfile } from "@api/models/UserProfile";
import { EventMembershipRepository } from "@api/repositories/EventMembershipRepository";
import { ObjectRepository } from "@api/repositories/ObjectRepository";
import { SettingRepository } from "@api/repositories/SettingRepository";
import { UserRepository } from "@api/repositories/UserRepository";
import { getCodeGenerator } from "@share/participants-import/helpers/codes";
import { UserCreateEditServiceInterface } from "./interfaces/UserCreateEditServiceInterface";

export class UserCreateEditService implements UserCreateEditServiceInterface {
    constructor(
        private readonly userRepository: UserRepository,
        private readonly settingRepository: SettingRepository,
        private readonly objectRepository: ObjectRepository,
        private readonly eventMembershipRepository: EventMembershipRepository
    ) {
    }

    async createUserAndAddToEvent(eventId: string, profileData: UserProfileInterface, userData: User) {

        const params = await this.getUserCreateParams(userData);
        const user = await this.userRepository.create(params);

        if (!user.id) {
            throw Error("user id is not exist");
        }

        const profileForCreate = {
            ...profileData,
            userId: user.id,
            code: user.username
        };

        await this.createProfile(profileForCreate);

        user.id && this.addUsersToEvent([user.id], eventId);
    }

    createProfile(profile: UserProfileInterface) {
        return this.objectRepository.create("UserProfiles", profile);
    }

    createProfilesBatch(items: UserProfileInterface[]) {
        return this.objectRepository.createBatch("UserProfiles", items);
    }

    updateProfile(profile: UserProfile) {
        return this.userRepository.saveProfile(profile);
    }

    updateUser(user: User) {
        if(!user.id) {
            throw new Error("userId is " + user.id);
        }

        return this.userRepository.saveUser(user.id, {
            language: user.language,
            notificationSettings: user.notificationSettings
        });
    }

    createUsersBatch(items: UserCreateParams[]) {
        return this.userRepository.createBatch(items);
    }

    /**
     * Возвращает пустую модель User с заданием языка
     * @param lang
     * @returns
     */
    getnewUser(lang: string) {
        return new User({ language: lang })
    }

    /**
     * добавить пользователей в мероприятие
     * @param userIds
     */
    async addUsersToEvent(userIds: number[], eventId: string) {
        this.eventMembershipRepository.create({
            model: {
                eventObjectId: eventId,
                eventSchemaId: process.env.VUE_APP_EVENTS_SCHEMA!,
                status: EventMembershipStatuses.confirmed,
                type: EventMembershipTypes.participant
            },
            userIds: userIds
        });
    }

    /**
     * Получение всех профилей в базе
     * @returns UserProfile[]
     */
    async getAllProfiles() {

        const query = {
            include: ['code'],
            skip: 0,
            take: -1
        }

        return this.userRepository.profiles(query);
    }

    async getCodeGenerationSettings() {
        return this.settingRepository.getProperties();
    }

    /**
     * Возвращает объект с полями необходимыми для создания нового пользователя
     */
    private async getUserCreateParams(userData: User) {
        let [allProfiles, codeGenerationSettings] = await Promise.all([
            this.getAllProfiles(),
            this.getCodeGenerationSettings()
        ]);

        const codes = new Set(allProfiles.map((profile) => profile.code));
        const getCode = getCodeGenerator(codes, codeGenerationSettings);

        if (!getCode) {
            throw new Error("code generator is not exist");
        }

        const code = getCode().toLowerCase();

        return {
            ...userData,
            password: code,
            username: code,
            roleId: "Authenticated"
        }
    }
}